/**
 * User profile route
 * Manage user settings and profile information
 */

import { createFileRoute } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLogout, authKeys } from '@/hooks/useAuth';
import { updateUser } from '@/lib/api/users';
import { storeSlug } from '@/lib/utils/localStorage';
import { formatDateFull } from '@/lib/utils/date';
import type { UpdateUserRequest } from '@/types/api';
import { requireAuth } from '@/lib/auth/routeGuards';

export const Route = createFileRoute('/profile/')({
  beforeLoad: requireAuth,
  component: ProfileComponent,
});

function ProfileComponent() {
  const { t } = useTranslation();
  // Get user from route context - guaranteed non-null by requireAuth
  const { user: currentUser } = Route.useRouteContext();
  // Use logout hook without triggering auth verification query
  const logout = useLogout();

  const queryClient = useQueryClient();

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: (data: { id: number; updates: UpdateUserRequest }) =>
      updateUser(data.id, data.updates),
    onSuccess: (updatedUser) => {
      // Update localStorage if slug changed
      if (updatedUser.slug !== currentUser.slug) {
        storeSlug(updatedUser.slug);
      }

      // Update auth cache
      queryClient.setQueryData(authKeys.verify(updatedUser.slug), {
        valid: true,
        user: updatedUser,
      });

      // Invalidate to refetch
      queryClient.invalidateQueries({
        queryKey: authKeys.all,
      });
    },
  });

  const form = useForm({
    defaultValues: {
      name: currentUser.name,
      defaultAvailability: currentUser.defaultAvailability || false,
    },
    onSubmit: async ({ value }) => {
      await updateUserMutation.mutateAsync({
        id: currentUser.id,
        updates: {
          name: value.name,
          defaultAvailability: value.defaultAvailability,
        },
      });
    },
  });

  const hasChanges = form.state.isDirty;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('profile.title')}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {t('profile.description')}
          </p>
        </div>

        {/* Success message */}
        {updateUserMutation.isSuccess && !hasChanges && (
          <div className="rounded-md bg-green-50 p-4 mb-6" role="alert">
            <div className="flex">
              <svg
                className="h-5 w-5 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  {t('profile.updateSuccess')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {updateUserMutation.isError && (
          <div className="rounded-md bg-red-50 p-4 mb-6" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {t('profile.updateError')}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {updateUserMutation.error instanceof Error
                    ? updateUserMutation.error.message
                    : t('profile.updateErrorMessage')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Profile card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('profile.accountInformation')}
            </h2>

            {/* Read-only fields */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('profile.userId')}
                </label>
                <p className="mt-1 text-sm text-gray-900">{currentUser.id}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('profile.slug')}
                </label>
                <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                  {currentUser.slug}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('profile.role')}
                </label>
                <span
                  className={`inline-flex items-center mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    currentUser.isOrganizer
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {currentUser.isOrganizer ? t('profile.organizer') : t('profile.member')}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('profile.memberSince')}
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {currentUser.createdAt
                    ? formatDateFull(new Date(currentUser.createdAt))
                    : t('profile.notAvailable')}
                </p>
              </div>
            </div>
          </div>

          {/* Editable form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="space-y-6 pt-6 border-t border-gray-200"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('profile.editProfile')}
            </h2>

            {/* Name field */}
            <form.Field name="name">
              {(field) => (
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('profile.displayName')}
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    disabled={updateUserMutation.isPending}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder={t('profile.displayNamePlaceholder')}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="mt-1 text-sm text-red-600" role="alert">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {t('profile.displayNameHelp')}
                  </p>
                </div>
              )}
            </form.Field>

            {/* Default availability toggle */}
            <form.Field name="defaultAvailability">
              {(field) => (
                <div>
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="defaultAvailability"
                        name="defaultAvailability"
                        type="checkbox"
                        checked={field.state.value}
                        onChange={(e) => field.handleChange(e.target.checked)}
                        disabled={updateUserMutation.isPending}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed"
                      />
                    </div>
                    <div className="ml-3">
                      <label
                        htmlFor="defaultAvailability"
                        className="text-sm font-medium text-gray-700"
                      >
                        {t('profile.defaultAvailable')}
                      </label>
                      <p className="text-xs text-gray-500">
                        {t('profile.defaultAvailableHelp')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </form.Field>

            {/* Action buttons */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => form.reset()}
                disabled={!hasChanges || updateUserMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('profile.reset')}
              </button>
              <button
                type="submit"
                disabled={!hasChanges || updateUserMutation.isPending}
                className="px-6 py-2 text-sm font-medium text-white bg-primary-700 rounded-md hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {updateUserMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {t('profile.saving')}
                  </span>
                ) : (
                  t('profile.saveChanges')
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Danger zone */}
        <div className="bg-white rounded-lg shadow-md p-6 border-2 border-red-200">
          <h2 className="text-lg font-semibold text-red-900 mb-4">{t('profile.dangerZone')}</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">{t('profile.logoutTitle')}</p>
              <p className="text-sm text-gray-500">
                {t('profile.logoutDescription')}
              </p>
            </div>
            <button
              type="button"
              onClick={logout}
              className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 transition-colors"
            >
              {t('profile.logout')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
